import type {
  DataProvider,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  PaginationPayload,
  RaRecord,
  SortPayload,
} from "react-admin";
import { DateTime } from "luxon";
import {
  Invoice as InvoiceDto,
  InvoiceEndpoint,
  InvoiceStateEnum,
  UserEndpoint,
  type PaymentModeEnum,
} from "../generated/backendClient";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { toSnakeCase } from "../utils/string";
import { getAuthenticatedBackendClient } from "../api/backend.api";
import { Cents } from "../model/Cents";
import { moneyToCents } from "../utils/moneyToCents";

type InvoiceTypeEnum = InvoiceDto["type"];

export interface Invoice extends RaRecord {
  id: string;
  nr: string;
  type: string;
  createdAt: DateTime;
  total: Cents;
  payment_amount?: Cents;
  refunded_amount?: Cents;
  paid_amount?: Cents;
  payment_mode: PaymentModeEnum | null;
  external_payment_type: string | null;
  status: InvoiceStateEnum;
  downloadUrl: string | null;
  payload: InvoiceDto;
  bookedTrainingIds: string[];
}

export class InvoicesProvider extends AbstractDataProvider<Invoice | UserBalanceItem> {
  async getOne(resource: string, { id }: GetOneParams): Promise<GetOneResult<Invoice | UserBalanceItem>> {
    if (resource === "b2cInvoices" || resource === "b2bInvoices") {
      const backendClient = await getAuthenticatedBackendClient();
      const invoice = await backendClient.invoice.invoiceRetrieve({ id });
      return { data: invoiceDtoToInvoice(invoice) };
    }
    throw new Error("Not implemented");
  }

  async getMany(resource: string, { ids }: GetManyParams): Promise<GetManyResult<Invoice | UserBalanceItem>> {
    const data: Array<Invoice | UserBalanceItem> = [];
    for (const id of ids) {
      const { data: record } = await this.getOne(resource, { id });
      data.push(record);
    }
    return { data };
  }

  async getList(resource: string, { pagination, sort, filter }: GetListParams): Promise<GetListResult<Invoice>> {
    return await fetchInvoices(filter, pagination, sort);
  }

  async getManyReference(
    resource: string,
    { id, target, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<Invoice | UserBalanceItem>> {
    let filter_ = { ...filter };
    if (resource === "studentBalance") {
      if (target !== "studentId") {
        throw new Error(`Unexpected target: ${JSON.stringify(target)} -- expected: "studentId"`);
      }
      return await fetchStudentBalance({ studentId: id + "" });
    }
    if (resource === "b2bInvoices") {
      if (target !== "drivingSchoolId") {
        throw new Error(`Unexpected target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
      }
      filter_ = { ...filter_, drivingSchoolId: id };
    } else if (resource === "b2cInvoices") {
      if (target !== "studentId") {
        throw new Error(`Unexpected target: ${JSON.stringify(target)} -- expected: "studentId"`);
      }
      filter_ = { ...filter_, studentId: id };
    }
    return this.getList(resource, { filter: filter_, sort, pagination });
  }
}

export const invoicesProvider = new InvoicesProvider() as DataProvider;

export type UserBalanceItem = Awaited<ReturnType<UserEndpoint["userBalanceOverviewRetrieve"]>>[0];
export type UserBalanceItemWithBalance = UserBalanceItem & { accuBalance: number; balanceChange: number };

async function fetchStudentBalance({
  studentId,
}: {
  studentId: string;
}): Promise<GetListResult<UserBalanceItemWithBalance>> {
  const client = await getAuthenticatedBackendClient();
  const results = (await client.user.userBalanceOverviewRetrieve({ id: studentId })).filter(
    // hide refunded prepaid invoices (they are not relevant for the balance overview, says ecki)
    (item) => !(item.invoice_type === "PREPAID_CREDITS" && item.invoice_state === "REFUNDED"),
  );

  // calculate the accumulated balance over time
  let accuBalance = 0;
  const resultsWithBalance: UserBalanceItemWithBalance[] = [];
  for (let i = results.length - 1; i >= 0; i--) {
    const balanceChange = parseFloat(results[i].balance_change.amount) * 100;
    accuBalance += balanceChange;
    resultsWithBalance.unshift({ ...results[i], accuBalance: accuBalance, balanceChange: balanceChange });
  }
  return {
    data: resultsWithBalance,
    total: results.length,
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
    },
  };
}

async function fetchInvoices(
  {
    drivingSchoolId,
    studentId,
    state,
    type,
    excludeType,
  }: {
    drivingSchoolId?: string;
    studentId?: string;
    state?: InvoiceStateEnum;
    type?: InvoiceTypeEnum;
    excludeType?: Array<InvoiceTypeEnum>;
  },
  { page, perPage }: PaginationPayload,
  sort: SortPayload,
): Promise<GetListResult<Invoice>> {
  const ordering = `${sort.order === "ASC" ? "" : "-"}${toSnakeCase(sort.field)}`;
  if (drivingSchoolId && studentId) {
    throw new Error("drivingSchoolId and student are are mutually exclusive");
  } else if (!drivingSchoolId && !studentId) {
    throw new Error("Neither drivingSchoolId nor studentId given");
  }

  let filter: Parameters<InvoiceEndpoint["invoiceList"]>[0];
  if (drivingSchoolId) {
    filter = { recipientCompanyDrivingSchoolId: drivingSchoolId, type: "B2B" };
  } else {
    filter = { recipientFirebaseAuthUsersId: studentId };
  }
  if (state) {
    filter = { ...filter, state };
  }
  if (type) {
    filter = { ...filter, type };
  }
  if (excludeType) {
    filter = { ...filter, excludeType };
  }

  const backendClient = await getAuthenticatedBackendClient();
  const { count, results, previous, next } = await backendClient.invoice.invoiceList({
    offset: (page - 1) * perPage,
    ordering,
    limit: perPage,
    ...filter,
  });

  return {
    data: results.map(invoiceDtoToInvoice),
    total: count,
    pageInfo: { hasPreviousPage: !!previous, hasNextPage: !!next },
  };
}

export function invoiceDtoToInvoice(invoiceDTO: InvoiceDto): Invoice {
  const {
    id,
    calculation_number,
    type,
    created_at,
    pdf,
    total_gross,
    state,
    driving_lesson_type,
    payment_amount,
    paid_amount,
    refunded_amount,
    payment_mode,
    external_payment_type,
    booked_training_id,
  } = invoiceDTO;
  let localizedType: string = type;
  if (type === "BASE_FEE") {
    localizedType = "für Grundbetrag";
  } else if (type === "TEACHING_MATERIAL") {
    localizedType = "für Lehrmittel";
  } else if (type === "DRIVING_LESSON_INVOICE") {
    if (driving_lesson_type === "normal") {
      localizedType = "für Fahrstunde";
    } else if (driving_lesson_type === "ueberlandfahrt") {
      localizedType = "für Überlandfahrt";
    } else if (driving_lesson_type === "autobahnfahrt") {
      localizedType = "für Autobahnfahrt";
    } else if (driving_lesson_type === "nachtfahrt") {
      localizedType = "für Nachtfahrt";
    } else if (driving_lesson_type === "praktischePruefung") {
      localizedType = "für Fahrprüfung";
    } else if (driving_lesson_type === "schaltkompetenz") {
      localizedType = "für Schaltkompetenz-Testfahrt";
    } else if (driving_lesson_type === "theoretischePruefung") {
      localizedType = "für Theorieprüfung";
    }
  } else if (type === "REMOTE_LESSON") {
    localizedType = "für Zoom-Meeting";
  } else if (type === "AUTHORITY_FEE") {
    localizedType = "für Prüfungsgebühr";
  } else if (type === "CREDIT_NOTE") {
    localizedType = "Erstattung";
  } else if (type === "PREPAID_CREDITS") {
    localizedType = "Vorauszahlung";
  }
  return {
    id,
    nr: calculation_number ?? id ?? "???",
    type: localizedType,
    createdAt: DateTime.fromISO(created_at),
    total: moneyToCents(total_gross),
    payment_amount: payment_amount.amount ? moneyToCents(payment_amount) : undefined,
    refunded_amount: refunded_amount.amount ? moneyToCents(refunded_amount) : undefined,
    paid_amount: paid_amount.amount ? moneyToCents(paid_amount) : undefined,
    payment_mode: payment_mode,
    external_payment_type: external_payment_type ?? null,
    status: state,
    downloadUrl: pdf,
    payload: invoiceDTO,
    accuBalance: 0,
    bookedTrainingIds: booked_training_id ?? [],
  };
}

export type OpenInvoice = Invoice & { payment_amount: number };

export function isOpenInvoice(invoice: Invoice): invoice is OpenInvoice {
  return typeof invoice.payment_amount === "number" && invoice.payment_amount > 0;
}

export function getOpenPaymentAgeInDays(invoice: Invoice, now = DateTime.now()): number | undefined {
  if (!isOpenInvoice(invoice) || !invoice.createdAt.isValid) {
    return undefined;
  }

  const today = now.setZone("Europe/Berlin").startOf("day");
  const createdAt = invoice.createdAt.setZone("Europe/Berlin").startOf("day");
  return Math.max(0, Math.floor(today.diff(createdAt, "days").days));
}

export function getOldestOpenPaymentAgeInDays(invoices: Invoice[], now = DateTime.now()): number | undefined {
  return invoices.reduce<number | undefined>((oldestOpenPaymentAgeInDays, invoice) => {
    const openPaymentAgeInDays = getOpenPaymentAgeInDays(invoice, now);
    if (
      openPaymentAgeInDays === undefined ||
      (oldestOpenPaymentAgeInDays !== undefined && openPaymentAgeInDays <= oldestOpenPaymentAgeInDays)
    ) {
      return oldestOpenPaymentAgeInDays;
    }
    return openPaymentAgeInDays;
  }, undefined);
}

export function getOpenPaymentAgeLabel(openPaymentAgeInDays: number): string {
  if (openPaymentAgeInDays === 0) {
    return "seit heute";
  }

  return `seit ${openPaymentAgeInDays} ${openPaymentAgeInDays === 1 ? "Tag" : "Tagen"}`;
}
